import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {fetchWithTracing} from '../../helpers/fetch-with-tracing'
import {useMutation, useIsMutating, useQueryClient, useQuery} from '@github-ui/react-query'
import type {QueryObserverResult} from '@github-ui/react-query'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {sleep} from '../../helpers/sleep'
import type {StackEntryPayload} from '../../page-data/payloads/merge-box'

type StackRebaseEntry = Omit<StackEntryPayload, 'status'>

type StackRebaseStatusPayload = {
  jobState: 'PENDING' | 'RUNNING' | 'DONE'
  pullRequestStates: Array<
    | {
        number: number
        state: 'SUCCEEDED' | 'CONFLICTED' | 'PENDING'
        message: null
      }
    | {
        number: number
        state: 'FAILED'
        message: string
      }
  >
}
export type StackRebasePullRequestStatus = StackRebaseStatusPayload['pullRequestStates'][0]['state']

type Input = {
  entries: StackRebaseEntry[]
}
type Callbacks = {
  onSuccess: () => void
  onError: (e: Error) => void
}

const mutationKey = ['mergebox-stack-rebase']

function useStackRebaseStatusQueryKey() {
  const {basePageDataUrl} = usePageDataContext()
  return [PageData.stackRebaseStatus, `basePageDataURL:${basePageDataUrl}`]
}

function useStackRebaseStatusQuery() {
  const statusURL = usePageDataUrl(PageData.stackRebaseStatus)
  const queryKey = useStackRebaseStatusQueryKey()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey,
    queryFn: async () => {
      return fetchWithTracing<StackRebaseStatusPayload>(statusURL, queryKey, queryClient)
    },
    staleTime: Infinity,

    // NOTE: this query is not run initially when the hook is called, instead
    // we explicitly call `refetch` from the mutation function.
    enabled: false,
  })
}

export function useStackRebaseStatus() {
  const isRebasing = useIsMutating({mutationKey}) > 0
  const {data} = useStackRebaseStatusQuery()

  const status = data?.pullRequestStates.reduce(
    (acc, pullState) => {
      acc[pullState.number] = pullState.state
      return acc
    },
    {} as Record<number, StackRebasePullRequestStatus>,
  )

  return {isRebasing, status: status ?? {}}
}

export function useStackRebaseMutation({onSuccess, onError}: Callbacks) {
  const apiURL = usePageDataUrl(PageData.stackRebase)
  const {refetch: refetchStatus} = useStackRebaseStatusQuery()
  const queryClient = useQueryClient()
  const statusQueryKey = useStackRebaseStatusQueryKey()

  return useMutation<Response, Error, Input>({
    mutationKey,
    mutationFn: async ({entries}) => {
      const expectedHeadOids = entries.reduce(
        (acc, entry) => {
          if (entry.pull.state !== 'MERGED') {
            acc[entry.pull.number.toString()] = entry.pull.headOid
          }
          return acc
        },
        {} as Record<string, string>,
      )

      // Clear any old state from a previous rebase so we don't show
      // stale results between now and when we first load the new status.
      queryClient.removeQueries({queryKey: statusQueryKey})

      return fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {expectedHeadOids},
      })
    },
    onSuccess: async data => {
      const json = await parseJSONWithBetterErrors(data)
      if (!data.ok) {
        throwErrorsIfBadResponse(data, json)
      }

      await pollForResults(refetchStatus)

      onSuccess()
    },
    onError: (e: Error) => {
      onError(e)
    },
  })
}

export const POLL_INITIAL_DELAY = 1000
const POLL_BACKOFF_MULTIPLIER = 1.5
const POLL_TIMEOUT = 600_000

async function pollForResults(
  fetchStatus: (options: {throwOnError: boolean}) => Promise<QueryObserverResult<StackRebaseStatusPayload>>,
  initialDelay = POLL_INITIAL_DELAY,
): Promise<void> {
  const startTime = Date.now()
  let delay = initialDelay

  for (;;) {
    const status = (await fetchStatus({throwOnError: true})).data
    if (!status) {
      throw new Error('Failed to load stack rebase status')
    }

    if (status.jobState === 'DONE') {
      return
    }

    if (Date.now() - startTime + delay > POLL_TIMEOUT) {
      throw new Error('Stack rebase polling timed out')
    }

    await sleep(delay)
    delay *= POLL_BACKOFF_MULTIPLIER
  }
}
